/**
 * SCRIPT mode, the deck family — IS A ONE-WORD LEGO'S GLOSS TRUE OUT OF CONTEXT? (job #497)
 *
 * Tom, 2026-09-27, on the premium decks: "I just got who = que, which is only
 * really true in the context of a whole phrase. The prompts should be the same
 * as in the spoken course. They should pass the ZUT test." The spoken course
 * teaches "que" as "who" inside seed 163, in a sentence; a Script card shows
 * the bare word with no sentence round it, and read that way "que" is "that",
 * "than", "what" far more often than it is "who".
 *
 * So a one-word LEGO joins the deck only where its own gloss holds across
 * everything the course teaches — its LEGOs, seeds, pod lines and BUILD/USE
 * phrases (never component rows, which are literal tiling glosses):
 *
 *   PRODUCTION (ZUT) — of the rows whose known side carries the gloss, the share
 *     whose target carries the word. Below the floor, the learner shown "you"
 *     has been taught to say something else ("usted = you": tú is the default).
 *   READING — of the rows whose target carries the word, the share whose known
 *     side carries the gloss ("to" optional, so "hablar = to speak" is read in
 *     "can I speak"). Below the floor, the word means something else in most of
 *     the course ("que = who": 7%).
 *
 * Both at their floors or better, or the item is left out. Multi-word LEGOs,
 * seeds, pod lines and out-and-about signs are whole units and are not tested.
 * English contractions are opened on both sides (it's → it is) so a gloss is
 * not missed for its spelling. A word counts as present in its mutated or
 * elided forms (the deck's own radicals) and, at four letters or more, with a
 * pronoun written onto its end (ayudar in ayudarte).
 *
 * Pure: no Vue.
 */

export interface ContextRow { known_text: string | null; target_text: string | null }

/**
 * The floors. Production is ZUT and must hold at least half the time. Reading
 * holds at a third: "heute = today" reads "tonight" in "heute Abend" and is still
 * today on a bare card (0.46), where "que = who" (0.07), "de = of" (0.15) and
 * "lo = it" (0.28) are not what the bare word means.
 */
export const PRODUCTION_FLOOR = 0.5
export const READING_FLOOR = 1 / 3

const OPEN: ReadonlyArray<[RegExp, string]> = [
  [/\b(\w+)n't\b/g, '$1 not'], [/\bcan not\b/g, 'cannot'], [/\bwo not\b/g, 'will not'], [/\bi'm\b/g, 'i am'],
  [/\b(\w+)'re\b/g, '$1 are'], [/\b(\w+)'ve\b/g, '$1 have'], [/\b(\w+)'ll\b/g, '$1 will'], [/\b(\w+)'d\b/g, '$1 would'],
  [/\b(it|that|what|there|he|she|who|where|here|how|let)'s\b/g, '$1 is'],
]

/** A text as lower-case tokens, English contractions opened. */
export function contextTokens(raw: string | null | undefined): string[] {
  let t = (raw ?? '').toLowerCase().replace(/[’‘ʼ`]/g, "'")
  for (const [re, to] of OPEN) t = t.replace(re, to)
  return t.replace(/[^\p{L}\p{M}\p{N}' -]+/gu, ' ').split(/[\s]+/).filter(Boolean)
}

const hasRun = (toks: readonly string[], run: readonly string[]): boolean => {
  if (!run.length) return false
  outer: for (let i = 0; i + run.length <= toks.length; i++) {
    for (let j = 0; j < run.length; j++) if (toks[i + j] !== run[j]) continue outer
    return true
  }
  return false
}

export interface GlossScore { production: number; reading: number; glossRows: number; wordRows: number }

/**
 * How far a one-word LEGO's gloss holds across the course. The index carries
 * the deck's own fold (Welsh mutation, Irish, French and Italian elision).
 */
export function scoreGloss(word: string, gloss: string, rows: readonly ContextEntry[]): GlossScore {
  const w = contextTokens(word)[0] ?? ''
  const g = contextTokens(gloss)
  const bare = g[0] === 'to' && g.length > 1 ? g.slice(1) : g
  // A known language written without spaces, or with its particles on the word
  // (Japanese, Chinese, Korean, the Dravidian languages), carries a gloss inside a
  // token: there the gloss is found as a run of text, not a run of words.
  const inText = !LATIN.test(g.join(''))
  // These search strings are fixed for every row in the course.
  const glossText = inText ? g.join(' ') : ''
  const bareText = bare === g ? glossText : inText ? bare.join(' ') : ''
  const holds = (r: ContextEntry, run: readonly string[], text: string) => (inText ? r.kText.includes(text) : hasRun(r.k, run))
  let glossRows = 0, glossHit = 0, wordRows = 0, wordHit = 0
  for (const r of rows) {
    const hasWord = r.folded.has(w) || (w.length >= 4 && r.t.some(t => t.startsWith(w)))
    const hasGloss = holds(r, g, glossText)
    if (hasGloss) { glossRows++; if (hasWord) glossHit++ }
    // A full gloss match also contains the bare infinitive: only search it after a miss.
    if (hasWord) { wordRows++; if (hasGloss || (bare !== g && holds(r, bare, bareText))) wordHit++ }
  }
  return {
    production: glossRows ? glossHit / glossRows : 1,
    reading: wordRows ? wordHit / wordRows : 1,
    glossRows,
    wordRows,
  }
}

const LATIN = /^[\p{Script=Latin}\p{M}\p{N}' -]*$/u

export interface ContextEntry { k: string[]; kText: string; t: string[]; folded: Set<string> }

/** The course's rows, tokenised and folded once for scoreGloss. */
export function contextIndex(rows: readonly ContextRow[], radicals: (w: string) => Set<string> = w => new Set([w])): ContextEntry[] {
  // Course sources repeat phrases. Reuse tokenization within this build only,
  // copying arrays so callers can still mutate each entry independently.
  const tokens = new Map<string, string[]>()
  const tokenize = (raw: string | null) => {
    const text = raw ?? ''
    let cached = tokens.get(text)
    if (!cached) tokens.set(text, (cached = contextTokens(text)))
    return cached.slice()
  }
  const memo = new Map<string, Set<string>>()
  const fold = (t: string) => { let f = memo.get(t); if (!f) memo.set(t, (f = radicals(t))); return f }
  return rows.map(r => {
    const t = tokenize(r.target_text)
    const folded = new Set<string>()
    for (const x of t) { folded.add(x); for (const y of fold(x)) folded.add(y) }
    const k = tokenize(r.known_text)
    return { k, kText: k.join(' '), t, folded }
  })
}

export const glossHolds = (s: GlossScore): boolean => s.production >= PRODUCTION_FLOOR && s.reading >= READING_FLOOR
